"use client";

import { useEffect, useRef, useState } from "react";
import { CLIENT_API_ROUTES } from "@/utils/clientApiRoutes";
import axios from "axios";
import Image from "next/image";
import { RxCross2 } from "react-icons/rx";
import { FaExternalLinkAlt } from "react-icons/fa";
import { MdPeople } from "react-icons/md";

function Team() {
  const [partnersByCategory, setPartnersByCategory] = useState([]);

  const [showMore, setShowMore] = useState(null);

  const popupContentRef = useRef(null);

  useEffect(() => {
    const getData = async () => {
      try {
        const [serRes] = await Promise.all([
          await axios.get(CLIENT_API_ROUTES?.GET_PARTNERS_BY_CATEGORY),
        ]);

        const partnersByCategory = serRes?.data?.partnersByCategory || [];
        setPartnersByCategory(partnersByCategory);
      } catch (err) {
        console.error("Error Fetching services by category:", err?.message);
      }
    };

    getData();
  }, []);

  // Run outside click logic ONLY when popup is shown
  useEffect(() => {
    if (!showMore) return;

    const handleOutsideClick = (e) => {
      if (
        popupContentRef.current &&
        !popupContentRef.current.contains(e.target)
      ) {
        setShowMore(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showMore]);

  return (
    <div className="w-[90%] md:w-[85%] mx-auto">
      {partnersByCategory?.length > 0 && (
        <>
          <div className="w-full">
            <div className="flex items-center justify-center gap-2">
              <p
                className="flex items-center gap-2 my-4 md:my-10 text-2xl md:text-3xl
              rounded-xl text-primary font-semibold border-2 border-primary px-4 py-2"
              >
                <MdPeople /> Our Team
              </p>
            </div>

            {partnersByCategory?.map((item, index) => (
              <div key={index}>
                <p className="w-full my-4 md:my-10 capitalize text-2xl md:text-3xl text-primary font-semibold text-center">
                  {item?.category}
                </p>

                {item?.partners?.length > 0 && (
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 md:gap-4 justify-start items-start">
                    {item?.partners?.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => setShowMore(item)}
                        className="bg-white shadow-xl cursor-pointer w-full min-h-[17rem] max-w-[18rem] rounded-lg p-2 md:p-4 justify-self-center flex flex-col items-center"
                      >
                        <div className="h-36 w-36 md:h-40 md:w-40 relative">
                          {item?.image && (
                            <Image
                              src={item.image}
                              alt="image"
                              fill
                              sizes="(max-width: 768px) 144px, 160px"
                              className="object-cover rounded-full"
                              loading="lazy"
                            />
                          )}
                        </div>

                        <div className="w-[95%] my-1 flex flex-col items-center justify-center">
                          <p className="text-lg md:text-xl font-medium text-black text-center  line-clamp-2">
                            {" "}
                            {item?.name}{" "}
                          </p>

                          {item?.designation && (
                            <p
                              className="line-clamp-2 px-2 md:px-4 rounded-full w-fit
                   mx-auto font-semibold text-primary  text-sm md:text-base text-center"
                            >
                              {item?.designation}{" "}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {showMore && (
        <>
          <div className="overflow-y-auto fixed inset-0 z-[10] bg-black/60 flex items-start justify-center mt-10 px-4 py-8">
            <div
              ref={popupContentRef}
              className="h-fit bg-white animate-fade-down animate-duration-300 w-full max-w-7xl 
              flex flex-col md:flex-row items-start justify-start mx-auto my-4 rounded-2xl overflow-hidden relative"
            >
              <div className="absolute h-10 w-10 top-3 right-1 overflow-hidden">
                <button
                  onClick={() => setShowMore(null)}
                  className="cursor-pointer flex justify-end hover:scale-110 transition-transform duration-300"
                >
                  <RxCross2 className="text-2xl md:text-3xl text-primary md:text-white" />
                </button>
              </div>

              {/* Right */}
              <div className="w-full md:w-[35%] animate-fade-down h-full  mt-10 md:py-10 flex flex-col items-center justify-start">
                {/* Photo */}
                <div className="h-[16rem] md:h-[20rem] w-[80%] mx-auto relative">
                  {showMore?.image && (
                    <Image
                      fill
                      sizes="(max-width: 768px) 80vw, 60vw"
                      loading="lazy"
                      src={showMore?.image}
                      alt="image"
                      className="object-contain"
                    />
                  )}
                </div>

                {/* Details */}
                <div className="w-[95%] my-2 md:my-4 flex flex-col items-center justify-center">
                  <p className="text-2xl md:text-3xl font-semibold text-primary text-center ">
                    {" "}
                    {showMore?.name}{" "}
                  </p>

                  {showMore?.designation && (
                    <p
                      className="line-clamp-1 px-2 md:px-4 rounded-full w-fit
                   mx-auto font-medium text-black text-lg md:text-xl text-center"
                    >
                      {showMore?.designation}{" "}
                    </p>
                  )}

                  {showMore?.link && (
                    <>
                      <a
                        href={showMore?.link}
                        target="_blank"
                        className="w-[95%] my-1 md:my-2 "
                      >
                        <p className="text-primary flex gap-2 items-center justify-center font-semibold text-base md:text-lg">
                          Learn More <FaExternalLinkAlt className="text-base" />
                        </p>
                      </a>
                    </>
                  )}
                </div>
              </div>

              {/* Left */}
              <div className="w-full md:w-[65%] min-h-[80vh] bg-primary  h-full py-4 md:py-12 flex flex-col  ">
                <p className="w-[90%] mx-auto animate-fade-down font-medium text-justify md:text-left text-base md:text-lg text-white/90">
                  {showMore?.description?.split("\n").map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Team;
